import { createTheme, type PaletteOptions, type Theme } from '@mui/material'
import { palette } from '../theme/modules/palette'
import { components } from './components'
import { snackbarStyles } from './snackbar'
import { darkPalette, lightPalette } from './tokens/palette'
import { radii } from './tokens/radii'
import { darkShadows, lightShadows } from './tokens/shadows'
import { spacing } from './tokens/spacing'
import { strokeWidth } from './tokens/strokeWidth'
import { typography } from './tokens/typography'

type FutureThemePalette = typeof lightPalette | typeof darkPalette
type FutureThemeShadows = typeof lightShadows | typeof darkShadows

type FutureTheme = {
  shadows: FutureThemeShadows
  strokeWidth: typeof strokeWidth
}

// Restricted to the futureTheme.* namespace — the legacy variant names in
// `tokens/typography.ts` are already declared as React.CSSProperties by the
// old theme's augmentation, and redeclaring them with literal types conflicts.
type CustomVariants = {
  [K in keyof typeof typography as K extends `futureTheme.${string}` ? K : never]: (typeof typography)[K]
}

type LeafPaths<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${Prefix}${K}` : LeafPaths<T[K], `${Prefix}${K}.`>
}[keyof T & string]

type FutureTextColors = LeafPaths<typeof lightPalette.text, 'futureTheme.text.'>

declare module '@mui/material/styles' {
  interface Theme {
    futureTheme: FutureTheme
  }
  interface ThemeOptions {
    futureTheme?: FutureTheme
  }
  interface Palette {
    futureTheme: FutureThemePalette
  }
  interface PaletteOptions {
    futureTheme?: FutureThemePalette
  }
  interface TypographyVariants extends CustomVariants {
    weights: typeof typography.weights
  }
  interface TypographyVariantsOptions extends Partial<CustomVariants> {
    weights?: typeof typography.weights
  }
}

declare module '@mui/system' {
  interface Shape {
    'futureTheme.radius-none': string
    'futureTheme.radius-xxs': string
    'futureTheme.radius-xs': string
    'futureTheme.radius-sm': string
    'futureTheme.radius-md': string
    'futureTheme.radius-lg': string
    'futureTheme.radius-xl': string
    'futureTheme.radius-2xl': string
    'futureTheme.radius-3xl': string
    'futureTheme.radius-4xl': string
    'futureTheme.radius-5xl': string
    'futureTheme.radius-full': string
  }
}

declare module '@mui/material/Typography' {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface TypographyPropsVariantOverrides extends Record<keyof CustomVariants, true> {
    // Disallows usage of MUI's default variants to encourage use of our variants
    // TODO - uncomment as these are tidied up
    //     h1: false
    //     h2: false
    //     h3: false
    //     h4: false
    //     h5: false
    //     h6: false
    //     subtitle1: false
    //     subtitle2: false
    //     body1: false
    //     body2: false
    //     caption: false
    //     button: false
    //     overline: false
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface TypographyPropsColorOverrides extends Record<FutureTextColors, true> {
    // Disallows usage of MUI's default colors to encourage use of futureTheme.text.*
    // TODO - uncomment as these are tidied up
    //     primary: false
    //     secondary: false
    //     success: false
    //     error: false
    //     info: false
    //     warning: false
    //     textPrimary: false
    //     textSecondary: false
    //     textDisabled: false
  }
}

export const theme = createTheme({
  spacing,
  typography,
  shape: { borderRadius: 4, ...radii },
  palette: {
    // For backwards compatibility
    ...palette,
    neutralLight: {
      ...palette.neutralLight,
      // Hack for easy transition
      300: lightPalette.border.subtle,
    },
    // Transition hack
    primary: {
      main: lightPalette.bg.brand.default,
      dark: lightPalette.bg.brand.hover,
      light: lightPalette.bg.brand.muted,
      [100]: lightPalette.bg.brand.muted_hover,
      contrastText: lightPalette.text.brand.onBrand,
    },
    text: {
      ...palette.text,
      primary: lightPalette.text.default,
      secondary: lightPalette.text.muted,
      hint: lightPalette.text.subtle,
      warning: {
        default: lightPalette.text.warning.default,
        onWarningMuted: lightPalette.text.warning.onWarningMuted,
      },
    },
    sidebar: {
      ...palette.sidebar,
      text: lightPalette.text.default,
      secondaryText: lightPalette.text.muted,
      icon: lightPalette.fg.muted,
      hoverBackground: lightPalette.bg.interactive.hover,
      selectedBackground: lightPalette.bg.interactive.active,
      headerBorder: lightPalette.border.subtle,
      divider: lightPalette.border.subtle,
      background: lightPalette.bg.surface.default,
      headerBackground: lightPalette.bg.surface.default,
      footerBackground: lightPalette.bg.surface.default,
    },
    header: {
      ...palette.header,
      text: lightPalette.text.default,
      secondary: lightPalette.text.muted,
    },
    skeleton: {
      light: lightPalette.fg.muted,
    },
    mode: 'light',
    futureTheme: lightPalette,
  } as PaletteOptions,
  futureTheme: { shadows: lightShadows, strokeWidth },
  components,
})

export const darkTheme = createTheme(theme, {
  palette: {
    // For backwards compatibility
    ...palette,
    neutralLight: {
      ...palette.neutralLight,
      // Hack for easy transition
      300: darkPalette.border.subtle,
    },
    primary: {
      main: darkPalette.bg.brand.default,
      dark: darkPalette.bg.brand.hover,
      light: darkPalette.bg.brand.muted,
      [100]: darkPalette.bg.brand.muted_hover,
      contrastText: darkPalette.text.brand.onBrand,
    },
    text: {
      ...palette.text,
      primary: darkPalette.text.default,
      secondary: darkPalette.text.muted,
      hint: darkPalette.text.subtle,
      warning: {
        default: darkPalette.text.warning.default,
        onWarningMuted: darkPalette.text.warning.onWarningMuted,
      },
    },
    sidebar: {
      ...palette.sidebar,
      text: darkPalette.text.default,
      secondaryText: darkPalette.text.muted,
      icon: darkPalette.fg.muted,
      hoverBackground: darkPalette.bg.interactive.hover,
      selectedBackground: darkPalette.bg.interactive.active,
      headerBorder: darkPalette.border.subtle,
      divider: darkPalette.border.subtle,
      background: darkPalette.bg.surface.default,
      headerBackground: darkPalette.bg.surface.default,
      footerBackground: darkPalette.bg.surface.default,
    },
    header: {
      ...palette.header,
      text: darkPalette.text.default,
      secondary: darkPalette.text.muted,
    },
    skeleton: {
      light: darkPalette.fg.muted,
    },
    mode: 'dark',
    futureTheme: darkPalette,
  } as PaletteOptions,
  futureTheme: { shadows: darkShadows, strokeWidth },
})

export const globalStyles = (theme: Theme) => ({
  body: {
    // Important to override globals.css - remove that later
    backgroundColor: `${theme.palette.futureTheme.bg.surface.subtle} !important`,
    color: theme.palette.futureTheme.text.default,
    // Important to override globals.css - remove that later
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  main: {
    // Important to override globals.css - remove that later
    backgroundColor: `${theme.palette.futureTheme.bg.surface.subtle} !important`,
  },
  html: {
    // Important to override globals.css - remove that later
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  // Only required while we have globals.css
  button: {
    fontFamily: `${theme.typography.fontFamily} !important`,
  },
  ...snackbarStyles(theme),
})
